// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import * as usersApi from "../api"

//Tiempo de vida del catálogo de usuarios en el store (30 minutos)
export const USERS_CACHE_TTL = 30 * 60 * 1000

const isLatest = (state, action) => state.requestId === action.meta.requestId

export const getUsers = createAsyncThunk('appUsers/getUsers', async (_, { rejectWithValue }) => {
    try {
        return {
            data: await usersApi.fetchUsers(),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, {
    //Evita peticiones duplicadas y reutiliza el caché mientras siga vigente
    condition: (_, { getState }) => {
        const users = getState()?.users

        if (!users) return true
        //Invalidación explícita (enableRequest): pasa aunque haya una petición en vuelo,
        //que pudo salir antes de la escritura; su respuesta se descarta por requestId
        if (users.isRequested) return true
        //Ya hay una petición en vuelo: los demás se cuelgan de esa
        if (users.status === 'pending') return false
        //Store vacío
        if (!users.data?.length) return true

        return Date.now() - (users.lastFetch ?? 0) > USERS_CACHE_TTL
    }
})

//El caché es por rol: el de getUsers describe la lista completa y reutilizarlo aquí
//abortaba la petición dejando el índice vacío.
export const getUsersByRole = createAsyncThunk('appUsers/getUsersByRole', async (roleId, { rejectWithValue }) => {
    try {
        return {
            data: await usersApi.fetchUsersByRole(roleId),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, {
    //Mismo criterio que getUsers, pero evaluado contra el estado de ese rol
    condition: (roleId, { getState }) => {
        //Comparación explícita para no descartar un id 0, igual que useUsersByRole
        if (roleId === null || roleId === undefined || roleId === '') return false

        const users = getState()?.users

        if (!users) return true

        const meta = users.byRoleMeta?.[roleId]

        //Invalidación explícita (invalidateUsersByRole): pasa aunque haya una petición en
        //vuelo para este rol; su respuesta se descarta por requestId
        if (meta?.isRequested) return true
        //Ya hay una petición en vuelo para este rol: los demás se cuelgan de esa
        if (meta?.status === 'pending') return false
        //Rol sin datos
        if (!users.byRole?.[roleId]?.length) return true

        return Date.now() - (meta?.lastFetch ?? 0) > USERS_CACHE_TTL
    }
})

export const getUserById = createAsyncThunk('appUsers/getUserById', async (userId, { rejectWithValue }) => {
    try {
        return {
            data: await usersApi.fetchUserById(userId),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, {
    //Mismo criterio que getUsersByRole, pero evaluado contra el estado de ese id
    condition: (userId, { getState }) => {
        //Comparación explícita para no descartar un id 0, igual que useUserById
        if (userId === null || userId === undefined || userId === '') return false

        const users = getState()?.users

        if (!users) return true

        const meta = users.byIdMeta?.[userId]

        //Invalidación explícita (invalidateUserById): pasa aunque haya una petición en
        //vuelo para este id; su respuesta se descarta por requestId
        if (meta?.isRequested) return true
        //Ya hay una petición en vuelo para este id: los demás se cuelgan de esa
        if (meta?.status === 'pending') return false
        //El TTL se mide sobre la última respuesta, no sobre `byId`: así un usuario que el
        //backend no encontró tampoco se vuelve a pedir en cada montaje
        if (!meta?.lastFetch) return true

        return Date.now() - meta.lastFetch > USERS_CACHE_TTL
    }
})

//Update userPassword
export const updateUserPassword = createAsyncThunk('appUsersPassword/updateUserPassword', async (user, { dispatch, rejectWithValue }) => {
    try {
        const data = await usersApi.updateUserPassword(user.userId, user.userData)
        await dispatch(getUsers())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})


export const appUsersSlice = createSlice({
    name: 'appUsers',
    initialState: {
        data: [],
        //Índice por rol, para que dos pantallas pidiendo roles distintos no se pisen
        byRole: {},
        //Estado del caché de cada rol: { [roleId]: { status, lastFetch, isRequested } }
        byRoleMeta: {},
        //Índice de usuarios traídos de uno en uno, para que varios componentes compartan el mismo
        byId: {},
        //Estado del caché de cada id: { [userId]: { status, lastFetch, isRequested, error, notFound } }
        byIdMeta: {},
        isRequested: true,
        status: 'idle',
        error: null,
        lastFetch: null,
        requestId: null
    },
    reducers: {
        resetUsersData: (state) => {
            state.data = []
        },
        enableRequest: (state) => {
            state.isRequested = true
        },
        //Equivalente a enableRequest pero acotado a un rol
        invalidateUsersByRole: (state, action) => {
            const meta = state.byRoleMeta[action.payload] ?? {}
            state.byRoleMeta[action.payload] = { ...meta, isRequested: true }
        },
        //Equivalente a enableRequest pero acotado a un usuario
        invalidateUserById: (state, action) => {
            const meta = state.byIdMeta[action.payload] ?? {}
            state.byIdMeta[action.payload] = { ...meta, isRequested: true }
        },
        disableRequest: (state) => {
            state.isRequested = false
        }
    },
    extraReducers: builder => {
        builder.addCase(getUsers.pending, (state, action) => {
            state.status = 'pending'
            state.error = null
            //Se consume aquí para que una invalidación durante la petición siga vigente
            state.isRequested = false
            state.requestId = action.meta.requestId
        })
        builder.addCase(getUsers.fulfilled, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'fulfilled'
            state.data = action.payload.data
            state.lastFetch = action.payload.fetchedAt
        })
        builder.addCase(getUsers.rejected, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'rejected'
            state.error = action.payload ?? action.error?.message ?? null
            //Permite reintentar en la siguiente llamada sin esperar al TTL
            state.isRequested = true
        })
        builder.addCase(getUsersByRole.pending, (state, action) => {
            const meta = state.byRoleMeta[action.meta.arg] ?? {}
            //isRequested se consume aquí para que una invalidación durante la petición siga vigente
            state.byRoleMeta[action.meta.arg] = { ...meta, status: 'pending', isRequested: false, error: null, requestId: action.meta.requestId }
        })
        builder.addCase(getUsersByRole.fulfilled, (state, action) => {
            const meta = state.byRoleMeta[action.meta.arg] ?? {}
            if (!isLatest(meta, action)) return
            state.byRole[action.meta.arg] = action.payload.data
            state.byRoleMeta[action.meta.arg] = { ...meta, status: 'fulfilled', lastFetch: action.payload.fetchedAt, error: null }
        })
        builder.addCase(getUsersByRole.rejected, (state, action) => {
            const meta = state.byRoleMeta[action.meta.arg] ?? {}
            if (!isLatest(meta, action)) return
            //Permite reintentar en la siguiente llamada sin esperar al TTL. El mensaje se
            //guarda aquí para que useUsersByRole lo derive del store, como hace getUsers
            state.byRoleMeta[action.meta.arg] = {
                ...meta,
                status: 'rejected',
                isRequested: true,
                error: action.payload ?? action.error?.message ?? null
            }
        })
        builder.addCase(getUserById.pending, (state, action) => {
            const meta = state.byIdMeta[action.meta.arg] ?? {}
            //isRequested se consume aquí para que una invalidación durante la petición siga vigente
            state.byIdMeta[action.meta.arg] = { ...meta, status: 'pending', isRequested: false, error: null, requestId: action.meta.requestId }
        })
        builder.addCase(getUserById.fulfilled, (state, action) => {
            const meta = state.byIdMeta[action.meta.arg] ?? {}
            if (!isLatest(meta, action)) return
            const [user] = Array.isArray(action.payload.data) ? action.payload.data : []

            if (user) {
                state.byId[action.meta.arg] = user
            }

            //notFound distingue "el backend respondió sin usuario" de "la petición falló":
            //sin esta bandera los dos casos se ven igual desde el hook (user undefined, sin error)
            state.byIdMeta[action.meta.arg] = {
                ...meta,
                status: 'fulfilled',
                lastFetch: action.payload.fetchedAt,
                error: null,
                notFound: !user
            }
        })
        builder.addCase(getUserById.rejected, (state, action) => {
            const meta = state.byIdMeta[action.meta.arg] ?? {}
            if (!isLatest(meta, action)) return
            //Permite reintentar en la siguiente llamada sin esperar al TTL
            state.byIdMeta[action.meta.arg] = {
                ...meta,
                status: 'rejected',
                isRequested: true,
                error: action.payload ?? action.error?.message ?? null
            }
        })
    }
})

export const { resetUsersData, enableRequest, disableRequest, invalidateUsersByRole, invalidateUserById } = appUsersSlice.actions


export const addUser = createAsyncThunk('appUsers/addUser', async (user, { dispatch, rejectWithValue }) => {
    try {
        const data = await usersApi.createUser(user)
        dispatch(enableRequest())
        await dispatch(getUsers())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//Update user
export const updateUser = createAsyncThunk('appUsers/updateUser', async (user, { dispatch, rejectWithValue }) => {
    try {
        const data = await usersApi.updateUser(user.userId, user.userData)
        dispatch(enableRequest())
        await dispatch(getUsers())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//Assign CashBox
export const assignCashBox = createAsyncThunk('appUsers/assignCashBox', async (user, { dispatch, rejectWithValue }) => {
    try {
        const data = await usersApi.assignCashBox(user.id, user)
        dispatch(enableRequest())
        await dispatch(getUsers())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export default appUsersSlice.reducer
